import { supabase } from '@/lib/supabase';
import { ticketService } from '@/services/ticketService';
import { TicketPriority, TicketCategory } from '@/types/ticket';
import { knowledgeSourceManager, QueryResult } from './knowledgeSourceManager';
import { costTrackingService } from './costTrackingService';
import { knowledgeSourceSettingsService } from './knowledgeSourceSettings';

// OpenAI API calls are now handled securely through Supabase Edge Functions
// The API key is stored in the Edge Function environment, not in frontend code

export interface ChatSession {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  title?: string;
  is_active: boolean;
  last_message_at?: string;
}

export interface ChatMessage {
  id: string;
  session_id: string;
  user_id: string;
  message: string;
  response?: string;
  created_at: string;
  metadata?: any;
  message_type?: 'text' | 'system' | 'error';
}

export class AIChatService {
  private cleanMarkdownFormatting(text: string): string {
    return text
      // Remove bold formatting (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      // Remove italic formatting (*text* or _text_)
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      // Remove code formatting (`text`)
      .replace(/`(.*?)`/g, '$1')
      // Remove headers (# ## ###) but preserve the text with line breaks
      .replace(/^#{1,6}\s+(.+)$/gm, '$1\n')
      // Convert list markers (- * +) to bullet points but preserve line structure
      .replace(/^[\s]*[-\*\+]\s+/gm, '• ')
      // Convert numbered list markers (1. 2. etc) to bullet points
      .replace(/^[\s]*\d+\.\s+/gm, '• ')
      // Preserve double line breaks as paragraph breaks
      .replace(/\n\s*\n/g, '\n\n')
      // Clean up excessive spaces on single lines but preserve line breaks
      .replace(/[ \t]+/g, ' ')
      // Remove leading/trailing spaces from each line but keep the line breaks
      .replace(/^[ \t]+|[ \t]+$/gm, '')
      .trim();
  }

  private detectExplicitTicketRequest(message: string): boolean {
    // Only detect when user explicitly asks to create a ticket
    const explicitTicketKeywords = [
      'create ticket', 'create a ticket', 'new ticket', 'open ticket', 'submit ticket',
      'file a ticket', 'open a support ticket', 'submit a support ticket'
    ];
    
    return explicitTicketKeywords.some(keyword => 
      message.toLowerCase().includes(keyword)
    );
  }

  private detectDetailedTicketInfo(message: string, chatHistory: ChatMessage[]): boolean {
    // Check if this appears to be detailed ticket information in response to our guidance
    const recentMessages = chatHistory.slice(-4); // Look at last 4 messages
    const askedForDetails = recentMessages.some(msg => 
      msg.response && msg.response.includes('I\'d be happy to help you create a support ticket')
    );
    
    if (!askedForDetails) return false;
    
    // Check if message contains detailed information
    const hasDetail = message.length > 50 && (
      message.toLowerCase().includes('problem') ||
      message.toLowerCase().includes('issue') ||
      message.toLowerCase().includes('error') ||
      message.toLowerCase().includes('expected') ||
      message.toLowerCase().includes('trying to') ||
      message.toLowerCase().includes('steps') ||
      message.toLowerCase().includes('reproduce')
    );
    
    return hasDetail;
  }

  private detectFollowUpQuestion(message: string, chatHistory: ChatMessage[]): boolean {
    const messageLower = message.toLowerCase();
    
    // Check for follow-up indicators
    const followUpIndicators = [
      'what are their names',
      'what are the names',
      'list them',
      'show them',
      'which ones',
      'those projects',
      'these projects',
      'the projects',
      'their names',
      'tell me more',
      'more details',
      'more info',
      'what about them',
      'elaborate',
      'can you list',
      'list the',
      'name them'
    ];
    
    // Check for pronouns that reference previous context
    const referencePronouns = ['those', 'these', 'them', 'their', 'the same', 'above'];
    
    const hasFollowUpIndicator = followUpIndicators.some(indicator => 
      messageLower.includes(indicator)
    );
    
    const hasReferencePronoun = referencePronouns.some(pronoun => 
      messageLower.includes(pronoun)
    );
    
    // Check if recent messages discussed projects or tickets
    const recentlyDiscussedData = chatHistory.slice(-3).some(msg => 
      msg.response && (
        msg.response.includes('project') ||
        msg.response.includes('ticket') ||
        msg.response.includes('found') ||
        msg.response.includes('waiting') ||
        msg.response.includes('pending')
      )
    );
    
    return (hasFollowUpIndicator || hasReferencePronoun) && recentlyDiscussedData;
  }

  private getPreviousContextData(chatHistory: ChatMessage[]): { projects?: any[], tickets?: any[] } {
    // Look through recent messages for stored context data
    const recentMessages = chatHistory.slice(-5).reverse(); // Last 5 messages, most recent first
    
    for (const msg of recentMessages) {
      if (msg.metadata && (msg.metadata.projects || msg.metadata.tickets)) {
        return {
          projects: msg.metadata.projects || [],
          tickets: msg.metadata.tickets || []
        };
      }
    }
    
    return {};
  }

  private mapCategoryFromMessage(message: string): TicketCategory {
    const categoryMap: { [key: string]: TicketCategory } = {
      'bug': TicketCategory.SWA_REPORTING_BUG,
      'broken': TicketCategory.SWA_REPORTING_BUG,
      'error': TicketCategory.SWA_REPORTING_BUG,
      'not working': TicketCategory.SWA_REPORTING_BUG,
      'feature': TicketCategory.SWA_FEATURE_REQUEST,
      'enhancement': TicketCategory.SWA_FEATURE_REQUEST,
      'request': TicketCategory.SWA_FEATURE_REQUEST,
      'site survey': TicketCategory.SST_ISSUE,
      'survey tool': TicketCategory.SST_ISSUE,
      'sst': TicketCategory.SST_ISSUE,
      'thryve': TicketCategory.THR_ISSUE,
      'thr': TicketCategory.THR_ISSUE,
      'door to door': TicketCategory.D2D_ISSUE,
      'd2d': TicketCategory.D2D_ISSUE,
      'team': TicketCategory.TEAM_ASSIGNMENT,
      'assignment': TicketCategory.TEAM_ASSIGNMENT,
      'missing data': TicketCategory.MISSING_PROJECT_DATA,
      'missing project': TicketCategory.MISSING_PROJECT_DATA
    };
    
    const messageLower = message.toLowerCase();
    for (const [keyword, category] of Object.entries(categoryMap)) {
      if (messageLower.includes(keyword)) {
        return category;
      }
    }
    
    return TicketCategory.GENERAL_SUPPORT_ISSUE;
  }

  private mapPriorityFromMessage(message: string): TicketPriority {
    const messageLower = message.toLowerCase();
    
    if (messageLower.includes('urgent') || messageLower.includes('critical') || messageLower.includes('immediate')) {
      return TicketPriority.URGENT;
    }
    if (messageLower.includes('high priority') || messageLower.includes('asap') || messageLower.includes('important')) {
      return TicketPriority.HIGH;
    }
    if (messageLower.includes('low priority') || messageLower.includes('when possible') || messageLower.includes('minor')) {
      return TicketPriority.LOW;
    }
    
    return TicketPriority.MEDIUM; // Default
  }

  private extractTicketTitle(message: string): string {
    // Try to extract a concise title from the message
    const sentences = message.split(/[.!?]+/);
    const firstSentence = sentences[0]?.trim();
    
    if (firstSentence && firstSentence.length > 0 && firstSentence.length <= 80) {
      // Clean up common conversational elements
      return firstSentence
        .replace(/^(i |can |could |please |help |create |submit |open |file )\w*/i, '')
        .replace(/\b(ticket|issue|bug|problem)\b/gi, '')
        .trim()
        .replace(/^(a |an |the )/i, '')
        .trim();
    }
    
    // Fallback: try to find the main subject
    const words = message.split(' ');
    if (words.length > 3) {
      return words.slice(0, 8).join(' ');
    }
    
    return message.length > 50 ? message.substring(0, 47) + '...' : message;
  }

  async createTicketFromMessage(message: string, userId: string): Promise<{ success: boolean; ticket?: any; error?: string }> {
    try {
      const title = this.extractTicketTitle(message);
      const priority = this.mapPriorityFromMessage(message);
      const category = this.mapCategoryFromMessage(message);
      
      const ticketInput = {
        title: title || 'Support Request',
        description: message,
        priority,
        category
      };
      
      console.log('AI Chat: Creating ticket with input:', ticketInput);
      
      const ticket = await ticketService.createTicket(ticketInput);
      
      return {
        success: true,
        ticket
      };
    } catch (error) {
      console.error('Error creating ticket from AI chat:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async createSession(userId: string): Promise<ChatSession | null> {
    try {
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({ 
          user_id: userId,
          title: `Chat ${new Date().toLocaleDateString()}`
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error creating chat session:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error in createSession:', error);
      return null;
    }
  }

  async getOrCreateSession(userId: string): Promise<ChatSession | null> {
    try {
      // Get active session or create new one
      const { data: existing, error: fetchError } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching session:', fetchError);
        return null;
      }
      
      if (existing) {
        // Update last message timestamp
        await supabase
          .from('chat_sessions')
          .update({ last_message_at: new Date().toISOString() })
          .eq('id', existing.id);
        return existing;
      }
      
      return this.createSession(userId);
    } catch (error) {
      console.error('Error in getOrCreateSession:', error);
      return null;
    }
  }

  async saveMessage(
    sessionId: string, 
    userId: string, 
    message: string, 
    response?: string,
    messageType: 'text' | 'system' | 'error' = 'text',
    contextData?: { projects?: any[], tickets?: any[] }
  ): Promise<ChatMessage | null> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          session_id: sessionId,
          user_id: userId,
          message,
          response: response || null,
          message_type: messageType,
          metadata: contextData || {}
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error saving message:', error);
        return null;
      }
      
      // Update session last message timestamp
      await supabase
        .from('chat_sessions')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', sessionId);
      
      return data;
    } catch (error) {
      console.error('Error in saveMessage:', error);
      return null;
    }
  }

  async getChatHistory(sessionId: string): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching chat history:', error);
        return [];
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getChatHistory:', error);
      return [];
    }
  }

  async getChatHistoryPaginated(
    sessionId: string, 
    limit: number = 10, 
    offset: number = 0
  ): Promise<{ messages: ChatMessage[], total: number, hasMore: boolean }> {
    try {
      // Get total count first
      const { count: totalCount, error: countError } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId);

      if (countError) {
        console.error('Error fetching message count:', countError);
        return { messages: [], total: 0, hasMore: false };
      }

      const total = totalCount || 0;

      // For getting the latest messages, we need to order descending and then reverse
      // This gets us the last N messages when offset is 0
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error fetching paginated chat history:', error);
        return { messages: [], total, hasMore: false };
      }
      
      // Reverse the messages to get chronological order
      const messages = (data || []).reverse();
      const hasMore = offset + limit < total;
      
      return { messages, total, hasMore };
    } catch (error) {
      console.error('Error in getChatHistoryPaginated:', error);
      return { messages: [], total: 0, hasMore: false };
    }
  }

  async getChatMessageCount(sessionId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId);
      
      if (error) {
        console.error('Error fetching message count:', error);
        return 0;
      }
      
      return count || 0;
    } catch (error) {
      console.error('Error in getChatMessageCount:', error);
      return 0;
    }
  }

  async getUserContext(userId: string): Promise<any> {
    try {
      // Get user profile with tenant information
      const { data: userProfile } = await supabase
        .from('users')
        .select(`
          *,
          tenants!tenant_id(
            tenant_id,
            company_name
          )
        `)
        .eq('id', userId)
        .single();
      
      // Get user's role first to determine access level
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('roles(id, name)')
        .eq('user_id', userId)
        .single();
      
      const roleId = (roleData as any)?.roles?.id;
      const roleName = (roleData as any)?.roles?.name || 'unknown';
      
      // Get project count based on role
      let projectCount = 0;
      
      if (roleId === 1 || roleName === 'Admin' || roleName === 'Super Admin') {
        // Admins have access to all projects
        const { count: totalCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });
        projectCount = totalCount || 0;
      } else {
        // Other users only see assigned projects or projects they follow
        const { count: assignedCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .or(`assigned_sales_rep_id.eq.${userId},assigned_internal_pm_id.eq.${userId},assigned_installer_pm_id.eq.${userId}`);
        projectCount = assignedCount || 0;
        
        // Also count projects they follow
        const { count: followCount } = await supabase
          .from('project_followers')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        projectCount += (followCount || 0);
      }
      
      // Get pipeline statistics for more context with user-friendly names
      const pipelineStats: any = {};
      if (roleId === 1 || roleName === 'Admin') {
        // Get counts by pipeline for admins with pipeline names
        const { data: pipelineData } = await supabase
          .from('projects')
          .select(`
            pipeline_id,
            pipelines!pipeline_id(
              id,
              name
            )
          `)
          .not('pipeline_id', 'is', null);
        
        if (pipelineData) {
          const pipelineCounts = pipelineData.reduce((acc: any, project: any) => {
            const pipelineName = project.pipelines?.name || `Pipeline ${project.pipeline_id}`;
            acc[pipelineName] = (acc[pipelineName] || 0) + 1;
            return acc;
          }, {});
          pipelineStats.by_pipeline = pipelineCounts;
        }
      }
      
      const tenantName = userProfile?.tenants?.company_name || 'SolerroPM';
      const firstName = userProfile?.first_name || '';
      const lastName = userProfile?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      return {
        user_id: userId,
        email: userProfile?.email,
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        role: roleName,
        role_id: roleId,
        projects_count: projectCount,
        pipeline_stats: pipelineStats,
        tenant_name: tenantName,
        tenant_id: userProfile?.tenant_id
      };
    } catch (error) {
      console.error('Error getting user context:', error);
      return { user_id: userId };
    }
  }

  async searchUserTickets(ticketFilter: string | null, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('roles(id, name)')
        .eq('user_id', userId)
        .single();
      
      const roleId = (roleData as any)?.roles?.id;
      const roleName = (roleData as any)?.roles?.name || 'unknown';
      
      let query = supabase
        .from('tickets')
        .select(`
          id,
          ticket_number,
          title,
          description,
          status,
          priority,
          internal_priority,
          category,
          created_at,
          updated_at,
          due_date,
          created_by,
          assigned_to,
          project_id
        `)
        .order('updated_at', { ascending: false })
        .limit(20);
      
      // Apply filters based on user query
      if (ticketFilter) {
        console.log('AI Chat: Applying ticket filter:', ticketFilter);
        
        switch (ticketFilter.toLowerCase()) {
          case 'open':
            query = query.in('status', ['open', 'in_progress']);
            break;
          case 'closed':
          case 'resolved':
            query = query.eq('status', 'resolved');
            break;
          case 'urgent':
          case 'high priority':
            query = query.in('priority', ['urgent', 'high']);
            break;
          case 'assigned to me':
          case 'my tickets':
            query = query.eq('assigned_to', userId);
            break;
        }
      }
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see tickets they created or are assigned to
        query = query.or(`created_by.eq.${userId},assigned_to.eq.${userId}`);
      }
      
      const { data: tickets, error } = await query;
      
      if (error) {
        console.error('Error searching user tickets:', error);
        return [];
      }
      
      console.log(`AI Chat: Raw ticket search results: ${tickets?.length || 0} tickets found`);
      
      if (tickets && tickets.length > 0) {
        console.log('AI Chat: Ticket status breakdown:', tickets.reduce((acc: any, t: any) => {
          const status = t.status || 'Unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {}));
        
        console.log('AI Chat: Ticket priority breakdown:', tickets.reduce((acc: any, t: any) => {
          const priority = t.priority || 'Unknown';
          acc[priority] = (acc[priority] || 0) + 1;
          return acc;
        }, {}));
      }
      
      if (!tickets || tickets.length === 0) {
        console.log('AI Chat: No tickets found for user:', userId);
        return [];
      }
      
      return tickets;
    } catch (error) {
      console.error('Error in searchUserTickets:', error);
      return [];
    }
  }

  async searchProjectsByStage(stageQuery: string, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('roles(id, name)')
        .eq('user_id', userId)
        .single();
      
      const roleId = (roleData as any)?.roles?.id;
      const roleName = (roleData as any)?.roles?.name || 'unknown';
      
      // Map stage queries to exact stage name patterns (based on actual dashboard stages)
      const stageMapping: { [key: string]: string[] } = {
        'permit': ['Permits Pending', 'Pending Permit', 'permit'],
        'pending permit': ['Permits Pending', 'Pending Permit'],
        'pending permits': ['Permits Pending', 'Pending Permit'],
        'install': ['Ready to Schedule Install', 'Install Scheduled', 'Live Install', 'Install Complete'],
        'ready to schedule': ['Ready to Schedule Install'],
        'ready to install': ['Ready to Schedule Install'],
        'ready to schedule install': ['Ready to Schedule Install'],
        'design': ['Design', 'Engineering', 'design review'],
        'design review': ['Design', 'Engineering'],
        'contract': ['Contract Signed', 'contract'],
        'contract signed': ['Contract Signed'],
        'pto': ['PTO Pending', 'PTO Granted', 'pto'],
        'pto complete': ['PTO Granted'],
        'pto pending': ['PTO Pending'],
        'waiting for approval': ['approval', 'waiting', 'pending approval'],
        'new leads': ['New Leads'],
        'credit': ['Credit Failed', 'Credit Pending']
      };
      
      console.log('AI Chat: Stage query detected:', stageQuery.toLowerCase());
      console.log('AI Chat: Mapped stage terms:', stageMapping[stageQuery.toLowerCase()] || [stageQuery.toLowerCase()]);
      
      const searchTerms = stageMapping[stageQuery.toLowerCase()] || [stageQuery.toLowerCase()];
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          current_stage_id,
          pipeline_id,
          project_street,
          project_city,
          project_state,
          project_zip_code,
          project_full_address,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          created_at,
          updated_at,
          stages:stages!current_stage_id(name),
          pipelines:pipelines!pipeline_id(name)
        `);
      
      // Build OR condition for stage names using proper filter syntax
      if (searchTerms.length === 1) {
        console.log('AI Chat: Searching for single stage term:', searchTerms[0]);
        query = query.filter('stages.name', 'ilike', `%${searchTerms[0]}%`);
      } else {
        // For multiple search terms, try each one individually and combine results
        console.log('AI Chat: Searching for multiple stage terms:', searchTerms);
        
        // Use the most specific match first, then fallback to broader terms
        let matchFound = false;
        for (const term of searchTerms) {
          if (term.length > 5) { // Prefer longer, more specific terms
            console.log('AI Chat: Trying specific stage term:', term);
            query = query.filter('stages.name', 'ilike', `%${term}%`);
            matchFound = true;
            break;
          }
        }
        
        // If no specific match, use the first term
        if (!matchFound) {
          console.log('AI Chat: Using first stage term as fallback:', searchTerms[0]);
          query = query.filter('stages.name', 'ilike', `%${searchTerms[0]}%`);
        }
      }
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see projects they're assigned to or follow
        
        // Get projects user follows
        const { data: followedProjects } = await supabase
          .from('project_followers')
          .select('related_entity_id')
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        const followedIds = followedProjects?.map(f => f.related_entity_id) || [];
        
        // Apply additional access filters
        if (followedIds.length > 0) {
          query = query.or(`assigned_sales_rep_id.eq.${userId},assigned_internal_pm_id.eq.${userId},assigned_installer_pm_id.eq.${userId},id.in.(${followedIds.join(',')})`);
        } else {
          query = query.or(`assigned_sales_rep_id.eq.${userId},assigned_internal_pm_id.eq.${userId},assigned_installer_pm_id.eq.${userId}`);
        }
      }
      
      const { data: projects, error } = await query;
      
      if (error) {
        console.error('Error searching projects by stage:', error);
        return [];
      }
      
      console.log(`AI Chat: Raw stage search results: ${projects?.length || 0} projects found`);
      
      if (projects && projects.length > 0) {
        console.log('AI Chat: Stage breakdown:', projects.reduce((acc: any, p: any) => {
          const stage = p.stages?.name || 'Unknown';
          acc[stage] = (acc[stage] || 0) + 1;
          return acc;
        }, {}));
      }
      
      if (!projects || projects.length === 0) {
        console.log('AI Chat: No projects found for stage query:', stageQuery);
        return [];
      }
      
      // Enhance projects with task and checklist data (same as other searches)
      const enhancedProjects = await Promise.all(
        projects.map(async (project: any) => {
          try {
            // Get tasks for this project
            const { data: tasks } = await supabase
              .from('tasks')
              .select(`
                id,
                title,
                description,
                status,
                priority,
                due_date,
                assigned_to,
                created_at,
                updated_at,
                completed_at
              `)
              .eq('project_id', project.id)
              .order('created_at', { ascending: false })
              .limit(20);
            
            // Get checklist items for this project
            const { data: checklistItems } = await supabase
              .from('project_checklist_items')
              .select(`
                id,
                is_completed,
                completed_at,
                value_text,
                value_date,
                assigned_user_id,
                template_item:checklist_items!template_item_id(
                  id,
                  item_name,
                  item_type,
                  is_required
                )
              `)
              .eq('project_id', project.id)
              .order('is_completed', { ascending: true });
            
            // Group checklist items by completion status
            const incompleteChecklists = checklistItems?.filter(item => !item.is_completed) || [];
            const completedChecklists = checklistItems?.filter(item => item.is_completed) || [];
            
            // Get pending/blocked tasks for quick insights
            const pendingTasks = tasks?.filter(task => task.status === 'pending' || task.status === 'blocked') || [];
            const urgentTasks = tasks?.filter(task => task.priority === 'urgent' || task.priority === 'high') || [];
            
            return {
              ...project,
              tasks: tasks || [],
              checklist_items: checklistItems || [],
              incomplete_checklists: incompleteChecklists,
              completed_checklists: completedChecklists,
              pending_tasks: pendingTasks,
              urgent_tasks: urgentTasks,
              total_tasks: tasks?.length || 0,
              incomplete_checklist_count: incompleteChecklists.length,
              completed_checklist_count: completedChecklists.length
            };
          } catch (enhanceError) {
            console.error(`Error enhancing project ${project.id}:`, enhanceError);
            return project;
          }
        })
      );
      
      return enhancedProjects;
    } catch (error) {
      console.error('Error in searchProjectsByStage:', error);
      return [];
    }
  }

  async searchProjectsByLocation(location: string, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('roles(id, name)')
        .eq('user_id', userId)
        .single();
      
      const roleId = (roleData as any)?.roles?.id;
      const roleName = (roleData as any)?.roles?.name || 'unknown';
      
      // Normalize location search terms (handle abbreviations)
      const locationMap: { [key: string]: string[] } = {
        'california': ['california', 'ca', 'calif'],
        'texas': ['texas', 'tx'],
        'florida': ['florida', 'fl', 'fla'],
        'new york': ['new york', 'ny', 'n.y.'],
        'arizona': ['arizona', 'az', 'ariz'],
        'nevada': ['nevada', 'nv', 'nev'],
        'utah': ['utah', 'ut'],
        'colorado': ['colorado', 'co', 'colo'],
        'oregon': ['oregon', 'or', 'ore'],
        'washington': ['washington', 'wa', 'wash']
      };
      
      // Find the full state name from the search term
      let stateToSearch = location.toLowerCase();
      for (const [fullName, variants] of Object.entries(locationMap)) {
        if (variants.includes(location.toLowerCase())) {
          stateToSearch = fullName;
          break;
        }
      }
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          current_stage_id,
          pipeline_id,
          project_street,
          project_city,
          project_state,
          project_zip_code,
          project_full_address,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          created_at,
          updated_at,
          stages:stages!current_stage_id(name),
          pipelines:pipelines!pipeline_id(name)
        `)
        .ilike('project_state', `%${stateToSearch}%`);
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see projects they're assigned to or follow
        
        // Get projects user follows
        const { data: followedProjects } = await supabase
          .from('project_followers')
          .select('related_entity_id')
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        const followedIds = followedProjects?.map(f => f.related_entity_id) || [];
        
        // Build OR condition for access
        const accessConditions = [
          `assigned_sales_rep_id.eq.${userId}`,
          `assigned_internal_pm_id.eq.${userId}`,
          `assigned_installer_pm_id.eq.${userId}`
        ];
        
        if (followedIds.length > 0) {
          accessConditions.push(`id.in.(${followedIds.join(',')})`)
        }
        
        query = query.or(accessConditions.join(','));
      }
      
      const { data: projects, error } = await query;
      
      if (error) {
        console.error('Error searching projects by location:', error);
        return [];
      }
      
      if (!projects || projects.length === 0) {
        return [];
      }
      
      // Enhance projects with task and checklist data (same as regular search)
      const enhancedProjects = await Promise.all(
        projects.map(async (project: any) => {
          try {
            // Get tasks for this project
            const { data: tasks } = await supabase
              .from('tasks')
              .select(`
                id,
                title,
                description,
                status,
                priority,
                due_date,
                assigned_to,
                created_at,
                updated_at,
                completed_at
              `)
              .eq('project_id', project.id)
              .order('created_at', { ascending: false })
              .limit(20);
            
            // Get checklist items for this project
            const { data: checklistItems } = await supabase
              .from('project_checklist_items')
              .select(`
                id,
                is_completed,
                completed_at,
                value_text,
                value_date,
                assigned_user_id,
                template_item:checklist_items!template_item_id(
                  id,
                  item_name,
                  item_type,
                  is_required
                )
              `)
              .eq('project_id', project.id)
              .order('is_completed', { ascending: true });
            
            // Group checklist items by completion status
            const incompleteChecklists = checklistItems?.filter(item => !item.is_completed) || [];
            const completedChecklists = checklistItems?.filter(item => item.is_completed) || [];
            
            // Get pending/blocked tasks for quick insights
            const pendingTasks = tasks?.filter(task => task.status === 'pending' || task.status === 'blocked') || [];
            const urgentTasks = tasks?.filter(task => task.priority === 'urgent' || task.priority === 'high') || [];
            
            return {
              ...project,
              tasks: tasks || [],
              checklist_items: checklistItems || [],
              incomplete_checklists: incompleteChecklists,
              completed_checklists: completedChecklists,
              pending_tasks: pendingTasks,
              urgent_tasks: urgentTasks,
              total_tasks: tasks?.length || 0,
              incomplete_checklist_count: incompleteChecklists.length,
              completed_checklist_count: completedChecklists.length
            };
          } catch (enhanceError) {
            console.error(`Error enhancing project ${project.id}:`, enhanceError);
            return project;
          }
        })
      );
      
      return enhancedProjects;
    } catch (error) {
      console.error('Error in searchProjectsByLocation:', error);
      return [];
    }
  }

  async searchProjects(searchTerm: string, userId: string): Promise<any[]> {
    try {
      // Get user's role to determine access
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('roles(id, name)')
        .eq('user_id', userId)
        .single();
      
      const roleId = (roleData as any)?.roles?.id;
      const roleName = (roleData as any)?.roles?.name || 'unknown';
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          project_name,
          company_name,
          current_stage_id,
          pipeline_id,
          project_street,
          project_city,
          project_state,
          project_zip_code,
          project_full_address,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          created_at,
          updated_at,
          stages:stages!current_stage_id(name),
          pipelines:pipelines!pipeline_id(name)
        `)
        .or(`project_name.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%,project_city.ilike.%${searchTerm}%,project_street.ilike.%${searchTerm}%,project_full_address.ilike.%${searchTerm}%`)
        .limit(10);
      
      // Apply access control based on role
      if (roleId !== 1 && roleName !== 'Admin' && roleName !== 'Super Admin') {
        // Non-admins can only see projects they're assigned to or follow
        
        // Get projects user follows
        const { data: followedProjects } = await supabase
          .from('project_followers')
          .select('related_entity_id')
          .eq('user_id', userId)
          .eq('related_entity_type', 'project');
        
        const followedIds = followedProjects?.map(f => f.related_entity_id) || [];
        
        // Build OR condition for access
        const accessConditions = [
          `assigned_sales_rep_id.eq.${userId}`,
          `assigned_internal_pm_id.eq.${userId}`,
          `assigned_installer_pm_id.eq.${userId}`
        ];
        
        if (followedIds.length > 0) {
          accessConditions.push(`id.in.(${followedIds.join(',')})`);
        }
        
        query = query.or(accessConditions.join(','));
      }
      
      const { data: projects, error } = await query;
      
      if (error) {
        console.error('Error searching projects:', error);
        return [];
      }
      
      if (!projects || projects.length === 0) {
        return [];
      }
      
      // Enhance projects with task and checklist data
      const enhancedProjects = await Promise.all(
        projects.map(async (project: any) => {
          try {
            // Get tasks for this project
            const { data: tasks } = await supabase
              .from('tasks')
              .select(`
                id,
                title,
                description,
                status,
                priority,
                due_date,
                assigned_to,
                created_at,
                updated_at,
                completed_at
              `)
              .eq('project_id', project.id)
              .order('created_at', { ascending: false })
              .limit(20);
            
            // Get checklist items for this project (focus on incomplete ones)
            const { data: checklistItems } = await supabase
              .from('project_checklist_items')
              .select(`
                id,
                is_completed,
                completed_at,
                value_text,
                value_date,
                assigned_user_id,
                template_item:checklist_items!template_item_id(
                  id,
                  item_name,
                  item_type,
                  is_required
                )
              `)
              .eq('project_id', project.id)
              .order('is_completed', { ascending: true }); // Show incomplete items first
            
            // Group checklist items by completion status
            const incompleteChecklists = checklistItems?.filter(item => !item.is_completed) || [];
            const completedChecklists = checklistItems?.filter(item => item.is_completed) || [];
            
            // Get pending/blocked tasks for quick insights
            const pendingTasks = tasks?.filter(task => task.status === 'pending' || task.status === 'blocked') || [];
            const urgentTasks = tasks?.filter(task => task.priority === 'urgent' || task.priority === 'high') || [];
            
            return {
              ...project,
              tasks: tasks || [],
              checklist_items: checklistItems || [],
              incomplete_checklists: incompleteChecklists,
              completed_checklists: completedChecklists,
              pending_tasks: pendingTasks,
              urgent_tasks: urgentTasks,
              total_tasks: tasks?.length || 0,
              incomplete_checklist_count: incompleteChecklists.length,
              completed_checklist_count: completedChecklists.length
            };
          } catch (enhanceError) {
            console.error(`Error enhancing project ${project.id}:`, enhanceError);
            // Return project without enhancement if there's an error
            return project;
          }
        })
      );
      
      return enhancedProjects;
    } catch (error) {
      console.error('Error in searchProjects:', error);
      return [];
    }
  }

  private async searchKnowledgeBase(
    message: string,
    userContext: any
  ): Promise<{ results: QueryResult[], context: string }> {
    try {
      if (!userContext.tenant_id) {
        return { results: [], context: '' };
      }

      // Generate embedding for the user's message
      const { data, error } = await supabase.functions.invoke('generate-embedding', {
        body: { text: message }
      });

      if (error || !data?.embedding) {
        console.warn('Failed to generate embedding for knowledge search:', error);
        return { results: [], context: '' };
      }

      // Track the embedding operation cost
      await costTrackingService.trackOperation(
        userContext.tenant_id,
        'embedding',
        'text-embedding-ada-002',
        Math.ceil(message.length / 4) // Approximate token count
      );

      // Initialize knowledge source manager
      await knowledgeSourceManager.initialize();

      // Use the new cascade search for confidence-based priority search
      const searchResult = await knowledgeSourceManager.cascadeSearch(
        data.embedding,
        message,
        userContext.tenant_id,
        10 // Max results
      );

      console.log('AI Chat: Cascade search results:', {
        resultsCount: searchResult.results.length,
        confidence: searchResult.confidence,
        sourcesSearched: searchResult.sourcesSearched
      });

      // Track the query operation
      await costTrackingService.trackOperation(
        userContext.tenant_id,
        'query',
        'vector-search',
        1 // Minimal cost for vector search
      );

      if (searchResult.results.length === 0) {
        return { results: [], context: '' };
      }

      // Build knowledge context for AI
      let context = '\n\nKNOWLEDGE BASE CONTEXT:\n';
      context += `Sources searched: ${searchResult.sourcesSearched.join(', ')}\n`;
      context += `Confidence level: ${(searchResult.confidence * 100).toFixed(1)}%\n\n`;
      
      searchResult.results.forEach((result, index) => {
        context += `\n[${index + 1}] Source: ${result.source_type} - ${result.document_title || 'Unknown'}\n`;
        context += `Content: ${result.chunk_text}\n`;
        if (result.citation) {
          context += `Citation: ${result.citation}\n`;
        }
        context += `Relevance: ${(result.similarity * 100).toFixed(1)}%\n`;
      });

      context += '\n\nInstructions: Use the above knowledge base content to enhance your response. ';
      
      // Add specific instructions based on confidence level
      if (searchResult.confidence >= 0.8) {
        context += 'The knowledge base has HIGH confidence information for this query. Base your response primarily on the provided context.';
      } else if (searchResult.confidence >= 0.7) {
        context += 'The knowledge base has MODERATE confidence information. Use the context to supplement your response.';
      } else if (searchResult.confidence < 0.5) {
        context += 'The knowledge base has LOW confidence information. You may need to provide a general response or ask for clarification.';
      }
      
      // Add citation requirement for sales training
      if (searchResult.sourcesSearched.includes('sales_training')) {
        context += ' Remember to include citations for any sales training content you reference.';
      }
      
      return { results: searchResult.results, context };
    } catch (error) {
      console.error('Error searching knowledge base:', error);
      return { results: [], context: '' };
    }
  }

  async sendMessage(
    message: string, 
    sessionId: string, 
    userId: string
  ): Promise<string> {
    try {
      // Get user context for better responses
      const userContext = await this.getUserContext(userId);
      
      // Get recent chat history for context (needed for ticket creation logic)
      const history = await this.getChatHistory(sessionId);
      const recentHistory = history.slice(-10); // Last 10 messages for context
      
      // Search knowledge base for relevant information
      const knowledgeSearch = await this.searchKnowledgeBase(message, userContext);
      const hasKnowledgeResults = knowledgeSearch.results.length > 0;
      
      // Check if this is a follow-up question about previous data
      const isFollowUpQuestion = this.detectFollowUpQuestion(message, recentHistory);
      
      // Get previous context data if this is a follow-up
      let previousContextData: { projects?: any[], tickets?: any[] } = {};
      if (isFollowUpQuestion) {
        previousContextData = this.getPreviousContextData(recentHistory);
        console.log('AI Chat: Detected follow-up question, retrieved previous context:', {
          projectCount: previousContextData.projects?.length || 0,
          ticketCount: previousContextData.tickets?.length || 0,
          message: message,
          hasProjects: !!previousContextData.projects,
          hasTickets: !!previousContextData.tickets
        });
        
        // Log sample project data for debugging
        if (previousContextData.projects && previousContextData.projects.length > 0) {
          console.log('AI Chat: Sample project from context:', previousContextData.projects[0]);
        }
      }
      
      // Check if the message is asking about specific projects or locations
      let projectData = previousContextData.projects || null;
      let ticketData = previousContextData.tickets || null;
      
      const projectKeywords = ['project', 'michael', 'lettera', 'clery', 'company', 'address', 'status', 'stage', 'tell me about', 'what about', 'show me', 'find', 'holding up', 'what\'s needed', 'next steps', 'blockers', 'blocker', 'stuck', 'progress', 'complete', 'done', 'finish', 'remaining'];
      const locationKeywords = ['california', 'texas', 'florida', 'new york', 'arizona', 'nevada', 'utah', 'colorado', 'oregon', 'washington', 'state', 'city', 'location', 'where', 'how many'];
      const stageKeywords = ['pending', 'permit', 'permits', 'install', 'installation', 'ready', 'scheduled', 'waiting', 'approval', 'approved', 'signed', 'contract', 'design', 'review', 'pto', 'complete', 'completed', 'stage', 'pipeline', 'status'];
      const ticketKeywords = ['ticket', 'tickets', 'support', 'issue', 'bug', 'problem', 'help', 'request', 'assigned', 'urgent', 'resolved', 'open', 'closed'];
      
      const isAskingAboutProject = projectKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const isAskingAboutLocation = locationKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const isAskingAboutStage = stageKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const isAskingAboutTickets = ticketKeywords.some(keyword => 
        message.toLowerCase().includes(keyword)
      );
      
      const hasExplicitTicketRequest = this.detectExplicitTicketRequest(message);
      const hasDetailedTicketInfo = this.detectDetailedTicketInfo(message, recentHistory);
      
      // If user has provided detailed ticket information, create the ticket
      if (hasDetailedTicketInfo) {
        console.log('AI Chat: User provided detailed ticket information, creating ticket');
        const ticketCreationResult = await this.createTicketFromMessage(message, userId);
        
        if (ticketCreationResult.success) {
          const ticket = ticketCreationResult.ticket;
          const successMessage = `Perfect! I've created ticket #${ticket.ticket_number} for you with all the details you provided.\n\nTicket Details:\n• Title: ${ticket.title}\n• Priority: ${ticket.priority}\n• Category: ${ticket.category}\n• Status: ${ticket.status}\n• Created: ${new Date(ticket.created_at).toLocaleDateString()}\n\nYour detailed ticket has been submitted to our support team. You'll receive updates as it's processed. You can view all your tickets by clicking the "Tickets" button in the chat header.`;
          
          await this.saveMessage(sessionId, userId, message, successMessage);
          return successMessage;
        } else {
          const errorMessage = `I tried to create a ticket for you, but encountered an error: ${ticketCreationResult.error || 'Unknown error'}. Please try again or contact support directly.`;
          await this.saveMessage(sessionId, userId, message, errorMessage, 'error');
          return errorMessage;
        }
      }
      
      // If user explicitly wants to create a ticket, ask for details
      if (hasExplicitTicketRequest) {
        const ticketCreationGuidance = `I'd be happy to help you create a support ticket. To make sure your issue gets resolved quickly, I'll need some additional information:\n\n• What specific problem are you experiencing?\n• What were you trying to do when the issue occurred?\n• What did you expect to happen?\n• What actually happened instead?\n• If it's a bug, what steps can someone follow to reproduce it?\n• How urgent is this issue? (low/medium/high/urgent)\n\nPlease provide as much detail as possible, and I'll create a well-documented ticket for you.`;
        
        await this.saveMessage(sessionId, userId, message, ticketCreationGuidance);
        return ticketCreationGuidance;
      }
      
      // Check if CRM data is enabled before searching
      const crmSettings = await knowledgeSourceSettingsService.getCRMSettings();
      const crmEnabled = await knowledgeSourceSettingsService.isSourceEnabled('crm_data');
      
      // Only fetch new data if CRM is enabled and we don't already have it from a follow-up question
      // Check specific toggles for projects vs tickets
      const shouldSearchProjects = crmEnabled && crmSettings.projects_enabled && 
        (isAskingAboutProject || isAskingAboutLocation || isAskingAboutStage);
      const shouldSearchTickets = crmEnabled && crmSettings.tickets_enabled && isAskingAboutTickets;
      
      if (!isFollowUpQuestion && (shouldSearchProjects || shouldSearchTickets)) {
        console.log('AI Chat: CRM enabled and user asking about data, message:', message, {
          crmEnabled,
          projectsEnabled: crmSettings.projects_enabled,
          ticketsEnabled: crmSettings.tickets_enabled,
          shouldSearchProjects,
          shouldSearchTickets
        });
        
        // Only search projects if projects toggle is enabled
        if (shouldSearchProjects) {
          // Extract potential search terms from the message
          // First try to find quoted terms
          const quotedTerms = message.match(/"([^"]+)"|'([^']+)'/g);
          if (quotedTerms && quotedTerms.length > 0) {
            const term = quotedTerms[0].replace(/["']/g, '');
            console.log('AI Chat: Searching with quoted term:', term);
            projectData = await this.searchProjects(term, userId);
          }
        }
        
        // If no quoted terms or no results, look for capitalized names  
        if (shouldSearchProjects && (!projectData || projectData.length === 0)) {
          // Look for consecutive capitalized words (proper names)
          const properNames = message.match(/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*/g) || [];
          console.log('AI Chat: Found proper names:', properNames);
          
          // Try to find names, but skip common words like "Project", "Commercial", "Tell"
          const skipWords = ['Tell', 'Project', 'Commercial', 'What', 'Can', 'How'];
          for (const term of properNames) {
            if (term.length > 2 && !skipWords.includes(term)) {
              console.log('AI Chat: Searching with proper name:', term);
              const projects = await this.searchProjects(term, userId);
              if (projects.length > 0) {
                projectData = projects;
                break;
              }
            }
          }
        }
        
        // If still no results, try extracting key parts of the message
        if (shouldSearchProjects && (!projectData || projectData.length === 0)) {
          // Remove common words and search with the rest
          const cleanedMessage = message
            .replace(/\b(tell|me|about|the|project|for|what|is|show|find|get|can|you)\b/gi, '')
            .trim();
          
          console.log('AI Chat: Searching with cleaned message:', cleanedMessage);
          if (cleanedMessage.length > 2) {
            projectData = await this.searchProjects(cleanedMessage, userId);
          }
        }
        
        console.log('AI Chat: Final projectData:', projectData?.length || 0, 'results');
      }
      
      // If no project data found but asking about location, do a location-based search
      if (shouldSearchProjects && (!projectData || projectData.length === 0) && isAskingAboutLocation) {
        console.log('AI Chat: Performing location-based search');
        
        // Extract location from message
        const locationTerms = ['california', 'ca', 'texas', 'tx', 'florida', 'fl', 'new york', 'ny', 'arizona', 'az', 'nevada', 'nv', 'utah', 'ut', 'colorado', 'co', 'oregon', 'or', 'washington', 'wa'];
        let detectedLocation = null;
        
        for (const term of locationTerms) {
          if (message.toLowerCase().includes(term)) {
            detectedLocation = term;
            break;
          }
        }
        
        if (detectedLocation) {
          projectData = await this.searchProjectsByLocation(detectedLocation, userId);
          console.log('AI Chat: Location search results:', projectData?.length || 0, 'projects');
        }
      }
      
      // If asking about stages/status and no specific project data found
      if (shouldSearchProjects && (!projectData || projectData.length === 0) && isAskingAboutStage) {
        console.log('AI Chat: Performing stage-based search');
        
        // Extract stage-related terms from message
        const stageTerms = ['pending permit', 'pending permits', 'ready to schedule', 'ready to install', 'waiting for approval', 'contract signed', 'design review', 'pto complete', 'installation complete'];
        let detectedStageQuery = null;
        
        for (const term of stageTerms) {
          if (message.toLowerCase().includes(term)) {
            detectedStageQuery = term;
            break;
          }
        }
        
        // If no specific phrase found, check for individual stage keywords
        if (!detectedStageQuery) {
          if (message.toLowerCase().includes('permit')) {
            detectedStageQuery = 'permit';
          } else if (message.toLowerCase().includes('install')) {
            detectedStageQuery = 'install';
          } else if (message.toLowerCase().includes('design')) {
            detectedStageQuery = 'design';
          } else if (message.toLowerCase().includes('contract')) {
            detectedStageQuery = 'contract';
          } else if (message.toLowerCase().includes('pto')) {
            detectedStageQuery = 'pto';
          }
        }
        
        if (detectedStageQuery) {
          projectData = await this.searchProjectsByStage(detectedStageQuery, userId);
          console.log('AI Chat: Stage search results:', projectData?.length || 0, 'projects');
        }
      }
      
      // If asking about tickets, search for user's tickets (only if not a follow-up with existing data)
      if (!isFollowUpQuestion && shouldSearchTickets) {
        console.log('AI Chat: Performing ticket search (tickets enabled)');
        
        // Extract ticket-related terms from message
        const ticketTerms = ['open', 'closed', 'resolved', 'urgent', 'high priority', 'assigned to me', 'my tickets'];
        let ticketFilter = null;
        
        for (const term of ticketTerms) {
          if (message.toLowerCase().includes(term)) {
            ticketFilter = term;
            break;
          }
        }
        
        console.log('AI Chat: Ticket filter detected:', ticketFilter || 'all tickets');
        ticketData = await this.searchUserTickets(ticketFilter, userId);
        console.log('AI Chat: Ticket search results:', ticketData?.length || 0, 'tickets');
      }
      
      // History already fetched above for ticket creation logic
      
      // Build messages array for OpenAI
      const messages = [
        {
          role: 'system',
          content: `You are ${userContext.first_name || userContext.full_name || 'the user'}'s personal assistant for ${userContext.tenant_name}'s solar project management system. You help with their daily tasks and questions about projects, workflows, and system features.${knowledgeSearch.context}

Current Date & Time: ${new Date().toLocaleString('en-US', { 
  weekday: 'long',
  year: 'numeric', 
  month: 'long', 
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short'
})}

User Context:
- Name: ${userContext.first_name || userContext.full_name || 'User'}
- Email: ${userContext.email}
- Company: ${userContext.tenant_name}
- Role: ${userContext.role}${userContext.role === 'Admin' ? ' (Full system access)' : ''}
- Total Projects Access: ${userContext.projects_count}
${userContext.pipeline_stats?.by_pipeline ? `- Projects by Pipeline: ${JSON.stringify(userContext.pipeline_stats.by_pipeline)}` : ''}

${projectData && projectData.length > 0 ? `
${isFollowUpQuestion ? 'IMPORTANT: This is a follow-up question about the projects you previously discussed. You MUST use the specific project data below to answer the user\'s question.\n\n' : ''}Relevant Project Data Found (${projectData.length} project${projectData.length !== 1 ? 's' : ''}):

${(isAskingAboutLocation || isAskingAboutStage) ? `${isAskingAboutLocation ? 'Location' : 'Stage'} Summary:
${(() => {
  const locationCounts: { [key: string]: number } = {};
  const stageCounts: { [key: string]: number } = {};
  const pipelineCounts: { [key: string]: number } = {};
  
  projectData.forEach((p: any) => {
    const state = p.project_state || 'Unknown';
    const stage = p.stages?.name || 'Unknown';
    const pipeline = p.pipelines?.name || 'Unknown';
    
    locationCounts[state] = (locationCounts[state] || 0) + 1;
    stageCounts[stage] = (stageCounts[stage] || 0) + 1;
    pipelineCounts[pipeline] = (pipelineCounts[pipeline] || 0) + 1;
  });
  
  return `- By State: ${Object.entries(locationCounts).map(([state, count]) => `${state} (${count})`).join(', ')}
- By Stage: ${Object.entries(stageCounts).map(([stage, count]) => `${stage} (${count})`).join(', ')}
- By Pipeline: ${Object.entries(pipelineCounts).map(([pipeline, count]) => `${pipeline} (${count})`).join(', ')}`;
})()}

` : ''}${projectData.slice(0, 10).map((p: any) => `
- Project: ${p.project_name}
  Company: ${p.company_name || 'N/A'}
  Location: ${p.project_city || ''}, ${p.project_state || 'N/A'}
  Current Stage: ${p.stages?.name || 'N/A'}
  Pipeline: ${p.pipelines?.name || 'N/A'}
  Created: ${new Date(p.created_at).toLocaleDateString()}
  Last Updated: ${new Date(p.updated_at).toLocaleDateString()}
  
  Current Tasks & Status:
  - Total Tasks: ${p.total_tasks || 0}
  - Pending/Blocked Tasks: ${p.pending_tasks?.length || 0}
  - Urgent/High Priority Tasks: ${p.urgent_tasks?.length || 0}
  ${p.pending_tasks?.length > 0 ? `
  Pending/Blocked Tasks:
${p.pending_tasks.slice(0, 5).map((task: any) => `    • ${task.title} (${task.status}) ${task.due_date ? `- Due: ${new Date(task.due_date).toLocaleDateString()}` : ''}`).join('\n')}` : ''}
  ${p.urgent_tasks?.length > 0 ? `
  Urgent/High Priority Tasks:
${p.urgent_tasks.slice(0, 5).map((task: any) => `    • ${task.title} (${task.priority}) ${task.due_date ? `- Due: ${new Date(task.due_date).toLocaleDateString()}` : ''}`).join('\n')}` : ''}
  
  Checklist Status:
  - Incomplete Checklist Items: ${p.incomplete_checklist_count || 0}
  - Completed Checklist Items: ${p.completed_checklist_count || 0}
  ${p.incomplete_checklists?.length > 0 ? `
  Outstanding Checklist Items:
${p.incomplete_checklists.slice(0, 8).map((item: any) => `    • ${item.template_item?.item_name || 'Unknown Item'} ${item.template_item?.is_required ? '(Required)' : ''}`).join('\n')}` : ''}
`).join('\n')}
` : ''}

${ticketData && ticketData.length > 0 ? `
Support Ticket Data Found (${ticketData.length} ticket${ticketData.length !== 1 ? 's' : ''}):

Ticket Summary:
${(() => {
  const statusCounts: { [key: string]: number } = {};
  const priorityCounts: { [key: string]: number } = {};
  
  ticketData.forEach((t: any) => {
    const status = t.status || 'Unknown';
    const priority = t.priority || 'Unknown';
    
    statusCounts[status] = (statusCounts[status] || 0) + 1;
    priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
  });
  
  return `- By Status: ${Object.entries(statusCounts).map(([status, count]) => `${status} (${count})`).join(', ')}
- By Priority: ${Object.entries(priorityCounts).map(([priority, count]) => `${priority} (${count})`).join(', ')}`;
})()}

Recent Tickets:
${ticketData.slice(0, 10).map((t: any) => `
• Ticket #${t.ticket_number}: ${t.title}
  Status: ${t.status || 'Unknown'}
  Priority: ${t.priority || 'Unknown'}
  Category: ${t.category || 'Unknown'}
  Created: ${new Date(t.created_at).toLocaleDateString()}
  Last Updated: ${new Date(t.updated_at).toLocaleDateString()}
  ${t.due_date ? `Due: ${new Date(t.due_date).toLocaleDateString()}` : ''}
`).join('')}
` : ''}

Important Context:
- You have access to project data based on the user's role and permissions
- Admins can see all projects, other users can only see projects they're assigned to or follow
- When asked about specific projects, provide the information you have access to
- Be specific and helpful with the project data provided
- You now have access to tasks and checklist items for projects to help answer questions about:
  * What's holding up a project (pending/blocked tasks, overdue items)
  * What needs to be done next (incomplete checklist items, urgent tasks)
  * Project status and progress (completion rates, due dates)
  * Specific blockers or bottlenecks
- Use the task and checklist data to provide actionable insights about project progress
- Highlight urgent or overdue items when relevant
- Mention specific incomplete checklist items when users ask what's needed
- For location-based queries (e.g., "How many projects in California?"), provide counts and summaries
- For stage-based queries (e.g., "How many projects are pending permit?", "How many projects are ready to install?"), provide stage-specific counts and details
- For ticket-based queries (e.g., "What are my open tickets?", "Do I have any urgent tickets?"), provide ticket summaries and details
- IMPORTANT: Always try to help solve the user's problem first using available data and knowledge
- Only offer to create a support ticket if you cannot resolve their issue or answer their question
- When users explicitly request to create a ticket, ask clarifying questions to gather enough detail
- Before creating any ticket, ensure you have: clear description, steps to reproduce (for bugs), expected vs actual behavior
- Never automatically create tickets - always help first, then offer ticket creation if needed
- When you cannot help solve an issue, suggest: "I can create a support ticket for you to get specialized help. Would you like me to do that?"
- If user provides detailed issue information after you've asked for ticket details, you may create the ticket
- When showing project data, include breakdowns by pipeline/stage if relevant
- When showing ticket data, include status, priority, and category information
- You have access to support tickets based on the user's role: admins see all tickets, others see only tickets they created or are assigned to
- Always respect the user's access level - non-admins only see their assigned/followed projects and relevant tickets

CRITICAL - Maintaining Conversation Continuity:
- When you mention specific numbers of projects or tickets in your response (e.g., "I found 4 projects waiting on permits"), you MUST be prepared to provide details about those specific items in follow-up questions
- If a user asks follow-up questions like "What are their names?" or "List them", refer to the specific project/ticket data that was provided in the context
- When project or ticket data is provided in the context above, use it to answer follow-up questions with specific details
- Always provide actual project names, companies, and details when available in the context - never say you don't have access if the data is provided
- If you mentioned finding projects/tickets in a previous response, and the user asks for more details, provide the specific information from the data given to you
- IMPORTANT: If "Relevant Project Data Found" appears in the context above with specific projects listed, YOU HAVE ACCESS to that data and MUST use it to answer questions
- Never apologize for not having access to data that is clearly provided in the context above
- When asked for project names, list them from the "Project:" fields in the data provided
- When asked about specific details, reference the actual data provided rather than giving generic responses

Personality & Tone:
- Address ${userContext.first_name || 'the user'} by their first name when appropriate to be friendly and personal
- Be helpful, knowledgeable, and professional but with a warm, approachable tone
- Sound like a capable personal assistant who knows their work well
- Be conversational but not overly casual - maintain professionalism
- When greeting, acknowledge the time of day (Good morning/afternoon/evening ${userContext.first_name || ''})
- Reference ${userContext.tenant_name} as their company when discussing company-specific features or data
- Provide accurate information based on their role and access level

Response Guidelines:
- Use plain text format without markdown formatting (no ** for bold, no * for italics)
- Use simple bullet points (•) for lists and organize them clearly with line breaks
- Break responses into readable paragraphs with double line breaks between sections
- Keep responses natural and conversational but well-structured
- For long responses with multiple data points, use paragraph breaks to separate different topics
- When listing project details, use bullet points with line breaks for easy reading

Example Follow-up Response Pattern:
- If you previously said "I found 3 projects ready for installation" and user asks "What are their names?"
- You MUST list the actual project names from the data provided, like:
  • Project: Solar Installation - Smith Residence
  • Project: Commercial Solar - ABC Corp
  • Project: Residential PV System - Johnson Home
- Never say "I don't have access to the names" when the data is clearly provided above`
        }
      ];
      
      // Add recent history
      recentHistory.forEach(msg => {
        if (msg.message) {
          messages.push({ role: 'user', content: msg.message });
        }
        if (msg.response) {
          messages.push({ role: 'assistant', content: msg.response });
        }
      });
      
      // Add current message
      messages.push({ role: 'user', content: message });
      
      // Call Supabase Edge Function (which securely calls OpenAI)
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke('openai-chat', {
        body: {
          messages,
          temperature: 0.7,
          max_tokens: hasKnowledgeResults ? 750 : 500 // More tokens when using knowledge base
        }
      });

      // Track completion operation cost if knowledge base was used
      if (hasKnowledgeResults) {
        const estimatedTokens = Math.ceil(JSON.stringify(messages).length / 4);
        await costTrackingService.trackOperation(
          userContext.tenant_id,
          'completion',
          'gpt-3.5-turbo',
          estimatedTokens
        );
      }

      if (response.error) {
        console.error('Edge Function error:', response.error);
        
        const errorMessage = 'I apologize, but I encountered an error. Please try again or contact support if the issue persists.';
        await this.saveMessage(sessionId, userId, message, errorMessage, 'error');
        return errorMessage;
      }

      const data = response.data;
      if (!data || !data.success) {
        console.error('Edge Function returned error:', data?.error);
        const errorMessage = data?.error || 'I apologize, but I couldn\'t generate a response.';
        await this.saveMessage(sessionId, userId, message, errorMessage, 'error');
        return errorMessage;
      }
      
      let aiResponse = data.message || 'I apologize, but I couldn\'t generate a response.';
      
      // Clean up markdown formatting for better readability in plain text
      aiResponse = this.cleanMarkdownFormatting(aiResponse);
      
      // Save message with response and context data
      const contextData: { projects?: any[], tickets?: any[] } = {};
      if (projectData && projectData.length > 0) {
        contextData.projects = projectData;
      }
      if (ticketData && ticketData.length > 0) {
        contextData.tickets = ticketData;
      }
      
      await this.saveMessage(
        sessionId, 
        userId, 
        message, 
        aiResponse,
        'text',
        Object.keys(contextData).length > 0 ? contextData : undefined
      );
      
      return aiResponse;
    } catch (error) {
      console.error('Error in sendMessage:', error);
      const errorMessage = 'I apologize, but I encountered an error. Please try again later.';
      await this.saveMessage(sessionId, userId, message, errorMessage, 'error');
      return errorMessage;
    }
  }

  async closeSession(sessionId: string): Promise<void> {
    try {
      await supabase
        .from('chat_sessions')
        .update({ is_active: false })
        .eq('id', sessionId);
    } catch (error) {
      console.error('Error closing session:', error);
    }
  }
}

export const aiChatService = new AIChatService();